import { useEffect, useRef } from "react";

interface ListEndProps {
  /** Whether the server has another page behind this one. */
  hasMore: boolean;
  /** A page is in flight right now. */
  loading: boolean;
  /** Ask for the next page. Must be referentially stable. */
  onReach: () => void;
}

/**
 * The bottom of a paged list, and the thing that asks for the next page.
 *
 * Two jobs, and the second is the one that is easy to leave out. It observes
 * itself into view to trigger the next fetch, and it says out loud when there
 * is nothing further -- because a list that simply stops is indistinguishable
 * from one that has broken. That was the original complaint: a queue ended at
 * a hundred rows with no control, no spinner and no last line, so the only
 * available reading was that the rest of the mail was missing.
 *
 * `rootMargin` fires the fetch 400px early, so the next hundred rows are
 * usually already in the DOM by the time the agent scrolls to where they go.
 * Waiting for the sentinel to actually be visible works, but shows a spinner
 * on every page, which reads as a slow list rather than a paged one.
 *
 * Not virtualised, deliberately. The rows carry inline selects and live SLA
 * clocks, and windowing them means reimplementing focus and selection across
 * an unmounting viewport for a queue that is realistically a few hundred rows
 * deep. If a view ever routinely runs to thousands, that is the point to
 * reach for a windowing library -- not before.
 */
export function ListEnd({ hasMore, loading, onReach }: ListEndProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasMore) return;

    /*
     * IntersectionObserver rather than a scroll handler: the list is inside a
     * scroll container that is not the window, so a scroll listener would have
     * to know which ancestor scrolls and recompute offsets on every event.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReach();
      },
      { rootMargin: "400px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onReach]);

  if (!hasMore) {
    return (
      <p className="px-3 py-6 text-center text-xs text-faint">
        End of list
      </p>
    );
  }

  return (
    <div ref={ref} className="px-3 py-6 text-center text-xs text-faint">
      {loading ? "Loading more…" : " "}
    </div>
  );
}
